import React, { useMemo, useState } from 'react';
import { first, isArray } from 'lodash';
import { ScaleTime } from 'd3-scale';

import { GridColumns, GridRows } from '@visx/grid';
import { AxisLeft, AxisRight } from '@visx/axis';
import { getStringWidth } from '@visx/text';
import { common, model } from '@gosupersimple/types';

import { localPoint } from '@visx/event';

import { notNil } from '@/lib/utils';
import { findClosestDate, getFormattedDateForPeriod } from '@/lib/date';
import { ChartAxis, ChartType, TimeAggregationPeriod } from '@/explore/types';
import { formatPropertyValue } from '@/explore/utils/format';
import { GroupFilter } from '@/explore/utils/drilldown';

import { ChartTooltip, renderGridColumns } from '../common';
import {
  getHighlightedTickValues,
  getScaleTimeBandwidth,
  getTickValues,
  GroupAxisHeight,
} from './utils';
import { GroupedTimeSeriesData, TimeSeriesData } from '../grouped-chart/types';
import { BarStack } from './bar-stack';
import { StackValues } from './stack-values';
import {
  getLeftRightAxisFormats,
  getLeftRightAxisTypes,
  getSeriesAxes,
} from '../grouped-chart/utils';

import { getScreenPoint } from '../utils';

import { useValueScales } from './use-value-scales';

import commonStyles, { gridLineColor, gridHighlightedLineColor } from '../charts.module.scss';

const NumYTicks = 7;
const YValuePaddingCoef = 0.2;

export type StackedChartData = {
  [key: string]: number | Date;
  date: Date;
}[];

export interface ChartColor {
  fill: string;
  highlight: string;
}

const buildGroupedTooltipData = (
  date: Date,
  group: string | undefined,
  axis: ChartAxis | undefined,
  position: { x: number; y: number },
  data: GroupedTimeSeriesData,
  timezone: string,
  aggPeriod: TimeAggregationPeriod | null,
): TooltipData | null => {
  const item = data.items.find(({ dateValue }) => dateValue.getTime() === date.getTime());

  if (item === undefined) {
    return null;
  }

  const groups = item.values
    .filter((item) => item.group === group)
    .map(({ group, values }) => ({
      label: `${group}: ${Object.values(values)
        .filter(notNil)
        .reduce((sum, value) => sum + value, 0)}`,
      points: data.series
        .filter((series) => series.axis === axis)
        .map(({ key, label, type, format, axis, chartType, color }) => ({
          label,
          value: values[key] ?? null,
          type,
          format,
          date: item.dateValue,
          color,
          axis,
          chartType,
        })),
    }));

  return {
    date,
    position,
    timezone,
    aggPeriod,
    groups,
  };
};

interface StackedGroupTimeSeriesChartProps {
  width: number;
  height: number;
  data: GroupedTimeSeriesData;
  dateScale: ScaleTime<number, number>;
  zonedDateScale: ScaleTime<number, number>;
  aggPeriod: TimeAggregationPeriod | null;
  highlightedDate: Date | null;
  groupKey: string | undefined;
  onTimeClick?: (params: {
    x: number;
    y: number;
    key: string;
    groupFilters: GroupFilter[];
  }) => void;
  onDateHighlight: (date: Date | null) => void;
  hideGrid: boolean;
  timezone: string;
  showTotals: boolean;
}

export const StackedGroupTimeSeriesChart = (props: StackedGroupTimeSeriesChartProps) => {
  const {
    width,
    height,
    data,
    dateScale,
    zonedDateScale,
    aggPeriod,
    onDateHighlight,
    hideGrid,
    timezone,
    showTotals,
  } = props;

  const bandwidth = getScaleTimeBandwidth(dateScale, aggPeriod);

  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null);

  const { axes, axisTypes, axisFormats } = useMemo(
    () => ({
      axes: getSeriesAxes(data.series),
      axisTypes: getLeftRightAxisTypes(data.series),
      axisFormats: getLeftRightAxisFormats(data.series),
    }),
    [data.series],
  );

  const valueScales = useValueScales({
    data,
    height,
    padding: YValuePaddingCoef,
    numTicks: NumYTicks,
    stacked: true,
  });

  const handleMouseMove = (
    event: React.TouchEvent<SVGRectElement> | React.MouseEvent<SVGRectElement>,
    group?: string,
    axis?: ChartAxis,
  ) => {
    const point = localPoint(event);
    if (point === null) {
      return;
    }

    const hoveredDate = new Date(dateScale.invert(point.x - bandwidth / 2));
    const dates = data.items.map((item) => item.dateValue);
    const closestDate = findClosestDate(dates, hoveredDate);

    onDateHighlight(closestDate ?? null);

    if (!closestDate) {
      return;
    }

    const dataItem = data.items.find(
      ({ dateValue }) => dateValue.getTime() === closestDate.getTime(),
    );
    setTooltipData(
      dataItem === undefined
        ? null
        : buildGroupedTooltipData(
            closestDate,
            group,
            axis,
            getScreenPoint(point, event.target as SVGElement),
            data,
            timezone,
            aggPeriod,
          ),
    );
    return;
  };

  const formatLeftAxisValue = (n: number) => {
    const value = axisTypes.left === 'Integer' ? Math.round(n) : Math.round(n * 100) / 100;
    return formatPropertyValue(value, {
      type: axisTypes.left,
      format: axisFormats.left,
      style: 'compact',
    });
  };

  const formatRightAxisValue = (n: number) => {
    const value = axisTypes.right === 'Integer' ? Math.round(n) : Math.round(n * 100) / 100;
    return formatPropertyValue(value, {
      type: axisTypes.right,
      format: axisFormats.right,
      style: 'compact',
    });
  };

  const yAxisWidths = {
    left: Math.max(
      ...valueScales.left
        .ticks(NumYTicks)
        .map((i) => formatLeftAxisValue(i.valueOf()))
        .map((i) => getStringWidth(i) ?? 0),
    ),
  };

  const domainSizes = {
    left: valueScales.left.domain()[1] - valueScales.left.domain()[0],
    right: valueScales.right.domain()[1] - valueScales.right.domain()[0],
  };

  // Default to right as main axis in case no series on left axis
  const primaryValueScale = domainSizes.left > 0 ? valueScales.left : valueScales.right;

  // Force right axis ticks to align with primary axis ticks
  const rightAxisTicks = primaryValueScale
    .ticks(NumYTicks)
    .map((tick) => valueScales.right.invert(primaryValueScale(tick)));

  const renderGroupedBarStacks = (chartData: GroupedTimeSeriesData) => {
    const width = dateScale.range()[1];
    const barGroupWidth = width / chartData.items.length;
    const barGroupInnerWidth = (width / chartData.items.length) * 0.7;
    const groups = chartData.items[0].values.map((v) => v.group);
    const numStacks = axes.length * groups.length;
    const barWidth = barGroupInnerWidth / numStacks;
    const barInnerWidth = barWidth * 0.8;

    return axes.map((axis, axisIdx) => {
      const keys = chartData.series
        .filter((s) => s.chartType === 'bar' && s.axis === axis)
        .map(({ key }) => key);

      const groups = chartData.items[0].values.map((v) => v.group);
      return groups.map((group, groupIdx) => {
        const idx = groupIdx + axisIdx * groups.length;
        const offset = -(barGroupInnerWidth / 2) + idx * barWidth + barWidth / 2;
        const axisDateScale = dateScale
          .copy()
          .range([dateScale.range()[0] + offset, dateScale.range()[1] + offset]);
        const chartData: TimeSeriesData = {
          ...data,
          items: data.items.map((item) => {
            const groupValues =
              (isArray(item.values)
                ? item.values.find((v) => v.group === group)?.values
                : item.values) ?? {};

            return {
              dateValue: item.dateValue,
              label: item.label,
              values: groupValues,
            };
          }),
        };

        return (
          <React.Fragment key={`bar-stack-${axis}-${group}`}>
            <BarStack
              keys={keys}
              data={chartData}
              valueScale={valueScales[axis]}
              dateScale={axisDateScale}
              offset={-(barGroupWidth / 2)}
              barWidth={barInnerWidth}
              group={group}
              onMouseMove={(event) => handleMouseMove(event as any, group, axis)}
              onMouseLeave={() => setTooltipData(null)}
            />
            <StackValues
              keys={keys}
              data={chartData}
              valueScale={valueScales[axis]}
              dateScale={axisDateScale}
              bar
              showTotals={showTotals}
            />
          </React.Fragment>
        );
      });
    });
  };

  const graphHeight = height + GroupAxisHeight;

  return (
    <div className={commonStyles.graph}>
      <svg width={width} height={graphHeight} className={commonStyles.graphMain}>
        {hideGrid ? null : (
          <>
            <GridRows
              width={width}
              height={height}
              scale={primaryValueScale}
              stroke={gridLineColor}
              tickValues={primaryValueScale.ticks(NumYTicks)}
            />
            <GridColumns
              width={width}
              height={height}
              scale={zonedDateScale}
              stroke={gridLineColor}
              tickValues={getTickValues(aggPeriod ?? 'hour', zonedDateScale)}
              // eslint-disable-next-line react/no-children-prop
              children={(lines) => renderGridColumns(lines, { width, strokeColor: gridLineColor })}
            />
            <GridColumns
              width={width}
              height={height}
              scale={zonedDateScale}
              stroke={gridHighlightedLineColor}
              tickValues={getHighlightedTickValues(aggPeriod ?? 'hour', zonedDateScale)}
              // eslint-disable-next-line react/no-children-prop
              children={(lines) =>
                renderGridColumns(lines, { width, strokeColor: gridHighlightedLineColor })
              }
            />
          </>
        )}

        {renderGroupedBarStacks(data)}

        {hideGrid ? null : (
          <>
            {domainSizes.left > 0 && (
              <AxisLeft
                left={yAxisWidths.left + 4}
                scale={valueScales.left}
                hideTicks
                hideAxisLine
                tickClassName={commonStyles.tickLabel}
                tickLabelProps={() => ({ dx: 4, dy: 10, textAnchor: 'end' })}
                tickValues={valueScales.left.ticks(NumYTicks)}
                tickFormat={(n) => formatLeftAxisValue(n.valueOf())}
              />
            )}
            {domainSizes.right > 0 && (
              <AxisRight
                left={width - 4}
                scale={valueScales.right}
                hideTicks
                hideAxisLine
                tickClassName={commonStyles.tickLabel}
                tickLabelProps={() => ({ dx: 0, dy: 10, x: 0, textAnchor: 'end' })}
                tickValues={rightAxisTicks}
                tickFormat={(n) => formatRightAxisValue(n.valueOf())}
              />
            )}
          </>
        )}
      </svg>

      <svg
        width={width}
        height={graphHeight}
        className={commonStyles.graphInteractive}
        style={{ pointerEvents: 'none' }}
      />

      {tooltipData && <Tooltip tooltipData={tooltipData} />}
    </div>
  );
};

type TooltipData = {
  date: Date;
  position: { x: number; y: number };
  timezone: string;
  aggPeriod: TimeAggregationPeriod | null;
  groups: {
    label: string;
    points: {
      label: string;
      value: number | null;
      type: model.PropertyType | null;
      format?: common.PropertyValueFormat;
      date: Date;
      color: string;
      axis: 'left' | 'right';
      chartType: ChartType;
    }[];
  }[];
};

const Tooltip = (props: { tooltipData: TooltipData }) => {
  const { tooltipData } = props;

  if (tooltipData.groups.length === 0) {
    return null;
  }

  const firstPoint = first(tooltipData.groups[0].points);
  const totalValueType = firstPoint?.type ?? 'Number';
  const totalValueFormat = firstPoint?.format;

  return (
    <ChartTooltip
      position={tooltipData.position}
      title={getFormattedDateForPeriod(
        tooltipData.date,
        tooltipData.aggPeriod,
        tooltipData.timezone,
      )}>
      <ul>
        {tooltipData.groups.length > 1 && (
          <li>
            Total:{' '}
            <strong>
              {formatPropertyValue(
                tooltipData.groups
                  .flatMap((group) => group.points.map((point) => point.value))
                  .filter(notNil)
                  .reduce((sum, value) => sum + value, 0),
                { type: totalValueType, format: totalValueFormat },
              )}
            </strong>
          </li>
        )}
        {tooltipData.groups.map((group, groupIdx) => (
          <li key={groupIdx} style={{ paddingTop: '5px' }}>
            <span>
              <strong>{group.label}</strong>
            </span>
            <ul>
              {group.points.map((point, j) => (
                <li key={j}>
                  <span className={commonStyles.seriesMarker} style={{ background: point.color }} />
                  {point.label ?? '-'}:{' '}
                  <strong>
                    {formatPropertyValue(point.value ?? '-', {
                      type: point.type,
                      format: point.format,
                    })}
                  </strong>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </ChartTooltip>
  );
};
